import React, { useEffect, useState } from 'react';

import { styled } from '@mui/material';
import _, { isArray } from 'lodash';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import Button from 'components/Button';
import DefaultFilterItem from 'components/Filters/DefaultFilterItem';
import Close from 'components/SvgComponents/icons/Close';
import globalMessages from 'translations/messages/global-messages';
import { cleanFiltersBeforeSave } from 'utils/filterUtils';
import localStorageUser from 'utils/localStorageUser';

const Div_FilterWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(4),
  '& .filters-wrapper': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .filters-wrapper-clear-button': {
      height: '36px !important',
      padding: `${theme.spacing(0, 4)} !important`,
      borderRadius: '24px !important',
    },
  },
}));

function FiltersWrap({
  intl,
  disabled,
  filterList = [],
  filterActions,
  modulesParams,
  hideResetFilter,
  localstorageFiltersKey,
  localStorageSetter,
  localStorageGetter,
  initialActiveFilters,
  onChange,
  onClearAllFilters,
}) {
  const [activeFilters, setActiveFilters] = useState();
  const tempActiveFilters = { ...activeFilters };

  useEffect(
    () => {
      if (activeFilters) {
        if (localstorageFiltersKey) {
          const filters = cleanFiltersBeforeSave(activeFilters);

          localStorageUser[localStorageSetter || 'saveFilterByProjectAndModule']({
            filters: activeFilters,
            ...localstorageFiltersKey,
          });
          onChange(filters);
        } else {
          onChange(activeFilters);
        }
      }
    },
    [activeFilters],
  );

  useEffect(() => {
    if (localstorageFiltersKey && localstorageFiltersKey) {
      const storedFilters = localStorageUser[localStorageGetter || 'getFilterByProjectAndModule'](
        localstorageFiltersKey,
      );
      if (storedFilters && Object.keys(storedFilters).length > 0) {
        setActiveFilters(storedFilters);
      } else {
        setActiveFilters(initialActiveFilters);
      }
    }
  }, []);

  const handleFilterChange = (filter, trackAnalytics, trackAnalyticsDisabled) => {
    Object.keys(filter).forEach(key => {
      delete tempActiveFilters[key];
    });
    const parsedFilter = JSON.parse(JSON.stringify(filter));
    const isDisabled = Object.keys(parsedFilter).length === 0;
    const tracker = (isDisabled && trackAnalyticsDisabled) || trackAnalytics;
    setActiveFilters({ ...tempActiveFilters, ...parsedFilter });
    if (tracker) tracker();
  };

  const handleFilterChangeByType = (
    filterValue,
    filterPropertyName,
    trackAnalytics,
    trackAnalyticsDisabled,
  ) => {
    const previousCount = tempActiveFilters[filterPropertyName]?.length || 0;
    let filterIndex = -1;
    if (tempActiveFilters[filterPropertyName]) {
      filterIndex = tempActiveFilters[filterPropertyName].findIndex(selectedFilter => {
        // match with id if exist
        if (filterValue.id && selectedFilter.id) {
          return filterValue.id === selectedFilter.id;
        }
        // match the object
        return _.isEqual(filterValue, selectedFilter);
      });
    }
    let tempFilters = [];
    if (filterIndex !== -1) {
      tempFilters = [
        ...tempActiveFilters[filterPropertyName].slice(0, filterIndex),
        ...tempActiveFilters[filterPropertyName].slice(
          filterIndex + 1,
          tempActiveFilters[filterPropertyName].length,
        ),
      ];
    } else if (isArray(filterValue)) {
      tempFilters = filterValue;
    } else {
      tempFilters = tempActiveFilters[filterPropertyName]
        ? [...tempActiveFilters[filterPropertyName], filterValue]
        : [filterValue];
    }

    if (!tempFilters.length) {
      delete tempActiveFilters[filterPropertyName];
    } else {
      tempActiveFilters[filterPropertyName] = tempFilters;
    }
    setActiveFilters(tempActiveFilters);
    const newCount = tempFilters.length || 0;
    if (newCount > previousCount) {
      if (trackAnalytics) trackAnalytics();
    } else if (newCount < previousCount) {
      if (trackAnalyticsDisabled) trackAnalyticsDisabled();
    }
  };

  const handleClearFilter = (filterPropertyName, trackAnalyticsDisabled) => {
    delete tempActiveFilters[filterPropertyName];
    setActiveFilters(tempActiveFilters);
    if (trackAnalyticsDisabled) trackAnalyticsDisabled();
  };

  const handleFilterClearByType = (filterPropertyName, trackAnalyticsDisabled) =>
    handleClearFilter(filterPropertyName, trackAnalyticsDisabled);

  const handleClearFilters = () => {
    setActiveFilters({});
    localStorageUser.setTmpNavigationItems({});
    if (onClearAllFilters) onClearAllFilters();
  };

  if (!filterList?.length) {
    return null;
  }
  const isReportsFilter = filterList.findIndex(object => object.key === 'split_by') > -1;

  const isResetDisabled =
    hideResetFilter ||
    (isReportsFilter &&
      Object.keys(tempActiveFilters)?.length === 1 &&
      tempActiveFilters.split_by === true) ||
    (!isReportsFilter && Object.keys(tempActiveFilters)?.length === 0);

  return (
    <Div_FilterWrapper>
      <div className="filters-wrapper">
        {!isResetDisabled && (
          <Button
            className="filters-wrapper-clear-button"
            variant="action"
            startIcon={<Close width={16} height={18} />}
            onClick={handleClearFilters}
          >
            {intl.formatMessage(globalMessages.clear_filters)}
          </Button>
        )}
        {filterList.map(
          ({
            component,
            key,
            label,
            subLabel,
            trackAnalytics,
            trackAnalyticsDisabled,
            props,
            ...filter
          }) => {
            const FILTER_COMPONENT = component || DefaultFilterItem;
            const { filterValue, filterPropertyName, filterPropertyArrayNames, active } = filter;

            if (filterPropertyArrayNames) {
              return (
                <FILTER_COMPONENT
                  key={key || filterValue?.id}
                  label={label}
                  subLabel={subLabel}
                  disabled={disabled}
                  filterPropertyArrayNames={filterPropertyArrayNames}
                  active={active}
                  activeFilters={tempActiveFilters}
                  filter={filterValue}
                  onChange={filter =>
                    handleFilterChange(filter, trackAnalytics, trackAnalyticsDisabled)
                  }
                  onClear={propName => handleClearFilter(propName, trackAnalyticsDisabled)}
                  modulesParams={modulesParams}
                  {...props}
                />
              );
            }
            if (filterPropertyName) {
              return (
                <FILTER_COMPONENT
                  key={key || filterValue?.id}
                  label={label}
                  subLabel={subLabel}
                  disabled={disabled}
                  activePropertyFilters={tempActiveFilters[filterPropertyName]}
                  filter={filterValue}
                  onChange={value =>
                    handleFilterChangeByType(
                      value,
                      filterPropertyName,
                      trackAnalytics,
                      trackAnalyticsDisabled,
                    )
                  }
                  onClear={() =>
                    handleFilterClearByType(filterPropertyName, trackAnalyticsDisabled)
                  }
                  modulesParams={modulesParams}
                  {...props}
                />
              );
            }

            return null;
          },
        )}
      </div>
      {filterActions}
    </Div_FilterWrapper>
  );
}

FiltersWrap.propTypes = {
  classes: PropTypes.object,
  intl: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  activeFilters: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  filterList: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
      filterPropertyName: PropTypes.string,
      filterValue: PropTypes.object,
    }),
  ),
  filterActions: PropTypes.element,
  modulesParams: PropTypes.object,
  active: PropTypes.string,
  hideResetFilter: PropTypes.bool,
  localstorageFiltersKey: PropTypes.object,
  localStorageSetter: PropTypes.string,
  localStorageGetter: PropTypes.string,
  initialActiveFilters: PropTypes.object,
  onChange: PropTypes.func,
  onClearAllFilters: PropTypes.func,
};

export default injectIntl(FiltersWrap);
